import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { STORAGE_KEYS, API_URL, AUTH_HEADERS } from "@/config";
import { useTenantFeConfig } from "@/contexts/TenantFeConfigContext";
import PasswordProtected from "@/components/auth/PasswordProtected";

interface PasswordProtectedGuardProps {
  children: React.ReactNode;
}

interface PasswordProtectedResponse {
  enabled: boolean;
  authenticated: boolean;
}

const ErrorState = ({ error }: { error: string }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      gap: 2,
      p: 3,
    }}
  >
    <Typography variant="h6" color="error">
      Access Denied
    </Typography>
    <Typography variant="body2" color="text.secondary" align="center">
      {error}
    </Typography>
  </Box>
);

function PasswordProtectedGuard({ children }: PasswordProtectedGuardProps) {
  const [isPasswordRequired, setIsPasswordRequired] = useState(false);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { tenantDomain, apiVersion } = useTenantFeConfig();

  const checkPasswordProtected =
    useCallback(async (): Promise<PasswordProtectedResponse | null> => {
      try {
        const passwordProtectedToken = localStorage.getItem(
          STORAGE_KEYS.PASSWORD_PROTECTED_TOKEN
        );

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          "x-tenant-domain": tenantDomain || "",
        };

        if (passwordProtectedToken) {
          headers[
            "Authorization"
          ] = `${AUTH_HEADERS.BEARER_PREFIX} ${passwordProtectedToken}`;
        }

        if (!tenantDomain) {
          return null;
        }

        const response = await fetch(
          `${API_URL}/${apiVersion}/settings/password-config`,
          {
            method: "GET",
            headers,
          }
        );

        if (!response.ok) {
          throw new Error("Failed to check password protection");
        }

        return await response.json();
      } catch (err) {
        console.error("Password protected check failed:", err);
        throw err;
      }
    }, [tenantDomain, apiVersion]);

  const verifyPassword = useCallback(
    async (password: string): Promise<boolean> => {
      try {
        const passwordProtectedToken = localStorage.getItem(
          STORAGE_KEYS.PASSWORD_PROTECTED_TOKEN
        );

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          "x-tenant-domain": tenantDomain || "",
        };

        if (passwordProtectedToken) {
          headers[
            "Authorization"
          ] = `${AUTH_HEADERS.BEARER_PREFIX} ${passwordProtectedToken}`;
        }

        const response = await fetch(
          `${API_URL}/${apiVersion}/settings/validate-end-user-password`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ password }),
          }
        );

        if (!response.ok) {
          return false;
        }

        const data = await response.json();
        if (data?.token) {
          localStorage.setItem(
            STORAGE_KEYS.PASSWORD_PROTECTED_TOKEN,
            data.token
          );
          return true;
        }
        return false;
      } catch (error) {
        console.error("Password validation error:", error);
        return false;
      }
    },
    [tenantDomain, apiVersion]
  );

  const handlePasswordVerified = useCallback(() => {
    setIsPasswordVerified(true);
    setIsPasswordRequired(false);
  }, []);

  useEffect(() => {
    const verifyPasswordProtected = async () => {
      try {
        setError(null);

        const response = await checkPasswordProtected();

        if (response) {
          if (response.enabled) {
            setIsPasswordVerified(response.authenticated);
            setIsPasswordRequired(!response.authenticated);
          } else {
            setIsPasswordVerified(true);
          }
        }
      } catch (err) {
        console.error("Password protected check failed:", err);
        setError("Failed to verify access. Please refresh the page.");
      }
    };

    verifyPasswordProtected();
  }, [checkPasswordProtected]);

  // Show error state
  if (error) return <ErrorState error={error} />;

  // Show password form if required and not verified - don't render children
  if (isPasswordRequired && !isPasswordVerified) {
    return (
      <PasswordProtected
        onPasswordVerified={handlePasswordVerified}
        verifyPassword={verifyPassword}
      />
    );
  }

  // Only render children when password is verified or not required
  if (isPasswordVerified) {
    return <>{children}</>;
  }

  // Fallback: don't render children if not verified
  return null;
}

export default PasswordProtectedGuard;
